import { connect, MapDispatchToProps, useDispatch } from 'react-redux';
import { Content } from '@visikon/core-models';
import { IState } from 'reducers/reducers';
import { VideoView, VideoViewSize } from 'components/mediaLibrary/videos/VideoView';
import { updateVideo, uploadVideoFile, uploadVideoSubtitles } from 'actions/MediaActions';
import { ObjectId } from '@visikon/core-models/base';
import { useRef } from 'react';

interface IPropsFromState {
  token?: string;
  languageFromState: Content.LanguageCode;
  mediaVariationFromState: ObjectId;
}
interface IPropsFromDispatch {
  addTag: (tags: Content.Tag[]) => void;
  removeTag: (tag: Content.Tag) => void;
  titleChange: (tilte: string) => void;
  uploadSubtitles: (video: Content.Video, language: Content.LanguageCode, variation: ObjectId, file: File) => void;
}

interface IOwnProps {
  video: Content.Video;
  onClick?: () => void;
  readOnly?: boolean;
  size?: VideoViewSize;
  showControls?: boolean;
  showThumbnail?: boolean;
  language?: Content.LanguageCode;
  mediaVariation?: ObjectId;
  id?: string;
}

function CVideoContainer(props: IPropsFromState & IPropsFromDispatch & IOwnProps) {
  const {
    onClick,
    addTag,
    removeTag,
    titleChange,
    readOnly,
    video,
    language,
    languageFromState,
    mediaVariation,
    mediaVariationFromState,
    id,
    showControls,
    size,
    uploadSubtitles,
    showThumbnail,
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useDispatch();

  const handleUploadVideo = (file: File) => {
    const activeLanguage = language || languageFromState;
    const activeVariation = mediaVariation ?? mediaVariationFromState;

    if (file) {
      dispatch(
        uploadVideoFile({
          video,
          language: activeLanguage,
          variation: activeVariation,
          file,
          duration: -1,
        }),
      );
      return;
    }

    const vid = videoRef.current;
    if (vid) {
      vid.src = URL.createObjectURL(file);
      vid.ondurationchange = () => {
        dispatch(
          uploadVideoFile({
            video,
            language: activeLanguage,
            variation: activeVariation,
            file,
            duration: vid.duration,
          }),
        );
      };
    }
  };

  return (
    <>
      <VideoView
        id={id}
        onClick={onClick}
        onTagAdded={addTag}
        onTagRemoved={removeTag}
        onTitleChange={titleChange}
        readOnly={readOnly}
        video={video}
        language={language || languageFromState}
        mediaVariation={mediaVariation || mediaVariationFromState}
        size={size}
        showControls={showControls}
        showThumbnail={showThumbnail}
        onUploadSubtitles={uploadSubtitles}
        onUploadVideo={handleUploadVideo}
      />
      <video ref={videoRef} width={0} height={0} />
    </>
  );
}

const mapStateToProps: (state: IState) => IPropsFromState = (state) => ({
  languageFromState: state.language.activeLanguage,
  mediaVariationFromState: state.language.activeMediaVariation,
});

const mapDispatchToProps: MapDispatchToProps<IPropsFromDispatch, IOwnProps> = (dispatch, ownProps) => ({
  addTag: (newTags: Content.Tag[]) => {
    const { video } = ownProps;

    const copy = JSON.parse(JSON.stringify(video));
    copy.tags = [...video.tags, ...newTags];
    dispatch(updateVideo(copy));
  },
  removeTag: (tag: Content.Tag) => {
    if (!tag) {
      return;
    }
    const { video } = ownProps;
    const tagIdx = video.tags.indexOf(tag);
    if (tagIdx >= 0) {
      const copy = JSON.parse(JSON.stringify(video));
      copy.tags.splice(copy.tags.indexOf(tag), 1);
      dispatch(updateVideo(copy));
    }
  },
  titleChange: (title: string) => {
    if (!title) {
      return;
    }
    const { video } = ownProps;
    const copy = JSON.parse(JSON.stringify(video));
    copy.name = title;
    dispatch(updateVideo(copy));
  },
  uploadSubtitles: (video, language, variation, file) => {
    dispatch(
      uploadVideoSubtitles({
        video,
        language,
        variation,
        file,
      }),
    );
  },
});

export const VideoContainer = connect(mapStateToProps, mapDispatchToProps)(CVideoContainer);
